<template>
  <div class="app-header-wrapper">
    <div v-if="!isTransparentPage" class="app-header-placeholder"></div>
    <header class="app-header" :class="[
			{ 'app-header--active': isMenuOpen },
			{ 'app-header--home': isTransparentPage && !isMenuOpen },
			{ 'app-header--scrolled': isScrolled },
			`app-header--${computedHeaderTheme}`
		]">

      <!-- Main Header Content (Visible when Closed) -->
      <div class="app-header__main" v-show="!isMenuOpen">
        <!-- Mobile Layout: Left group (menu + sitename) -->
        <div class="app-header__mobile-left" v-if="deviceType === 'mobile'">
          <button class="app-header__menu-btn" @click="toggleMenu">
            <img src="@/assets/default/menu-icon.svg" alt="Menu" class="app-header__icon">
          </button>
          <a :href="getPageUrl('/')" class="app-header__logo-text">
            {{ siteNameText }}
          </a>
        </div>

        <!-- Desktop Layout: Three column -->
        <template v-else>
          <div class="app-header__left">
            <button class="app-header__menu-btn" @click="toggleMenu">
              <img src="@/assets/default/menu-icon.svg" alt="Menu" class="app-header__icon">
            </button>
          </div>

          <a :href="getPageUrl('/')" class="app-header__logo">
            <div class="app-header__logo-text">
              {{ siteNameText }}
            </div>
          </a>
        </template>

        <!-- Right: Action Button -->
        <div class="app-header__right">
          <a :href="getPageUrl('/cities')" class="app-header__cta-btn">
            <span class="app-header__cta-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16"
                height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M7.99998 8.87999C8.48797 8.87999 8.87999 8.48399 8.87999 7.99998C8.87999 7.51597 8.488 7.11997 7.99998 7.11997C7.51597 7.11997 7.11997 7.51597 7.11997 7.99998C7.11997 8.48399 7.51601 8.87999 7.99998 8.87999Z"
                  fill="black" />
                <path
                  d="M7.99998 0C3.58001 0 0 3.58001 0 7.99998C0 12.416 3.58001 16 7.99998 16C12.42 16 16 12.416 16 7.99998C16 3.58001 12.42 0 7.99998 0ZM9.75198 9.75198L3.19999 12.8L6.24798 6.24798L12.8 3.19999L9.75198 9.75198Z"
                  fill="black" />
              </svg></span>
            <span>{{ navExploreText }}</span>
          </a>
        </div>
      </div>

      <!-- Full Screen Backdrop for click-outside to close -->
      <transition name="fade">
        <div class="app-header__backdrop" v-if="isMenuOpen" @click="closeMenu"></div>
      </transition>

      <!-- Full Screen Overlay Menu (Visible when Open) -->
      <transition name="fade">
        <div class="app-header__overlay" v-if="isMenuOpen">

          <!-- Overlay Header -->
          <div class="app-header__overlay-header">
            <!-- Left: Close Button + Logo (for mobile layout) -->
            <div class="app-header__overlay-left">
              <button class="app-header__menu-btn" @click="toggleMenu">
                <img src="@/assets/default/menu-icon-close.svg" alt="Close"
                  class="app-header__icon app-header__icon--close">
              </button>
              <div class="app-header__logo-text app-header__logo-text--white">
                {{ siteNameText }}
              </div>
            </div>

            <!-- Right: Explore Button (Yellow Pill) -->
            <div class="app-header__overlay-right">
              <a :href="getPageUrl('/cities')"
                class="app-header__cta-btn app-header__cta-btn--yellow">
                <span class="app-header__cta-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16"
                    height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M7.99998 8.87999C8.48797 8.87999 8.87999 8.48399 8.87999 7.99998C8.87999 7.51597 8.488 7.11997 7.99998 7.11997C7.51597 7.11997 7.11997 7.51597 7.11997 7.99998C7.11997 8.48399 7.51601 8.87999 7.99998 8.87999Z"
                      fill="black" />
                    <path
                      d="M7.99998 0C3.58001 0 0 3.58001 0 7.99998C0 12.416 3.58001 16 7.99998 16C12.42 16 16 12.416 16 7.99998C16 3.58001 12.42 0 7.99998 0ZM9.75198 9.75198L3.19999 12.8L6.24798 6.24798L12.8 3.19999L9.75198 9.75198Z"
                      fill="black" />
                  </svg></span>
                <span>{{ navExploreText }}</span>
              </a>
            </div>
          </div>

          <!-- Overlay Content -->
          <div class="app-header__overlay-content">

            <!-- Menu Grid (2x2 for Mobile, Row for Desktop) -->
            <div class="app-header__menu-grid">
              <a :href="getPageUrl('/about-us')" class="app-header__menu-pill">
                <img src="@/assets/default/about-icon.svg" class="app-header__menu-icon" />
                <span>{{ navMenuAbout }}</span>
              </a>
              <a :href="getPageUrl('/terms-of-service')" class="app-header__menu-pill">
                <img src="@/assets/default/terms-icon.svg" class="app-header__menu-icon" />
                <span>{{ navMenuTerms }}</span>
              </a>
              <a :href="getPageUrl('/privacy-policy')" class="app-header__menu-pill">
                <img src="@/assets/default/privacy-icon.svg" class="app-header__menu-icon" />
                <span>{{ navMenuPrivacy }}</span>
              </a>
              <a :href="getPageUrl('/disclaimer')" class="app-header__menu-pill">
                <img src="@/assets/default/disclaimer-icon.svg" class="app-header__menu-icon" />
                <span>{{ navMenuDisclaimer }}</span>
              </a>
            </div>

            <!-- Featured Cities -->
            <div class="app-header__featured">
              <a class="app-header__city-card" v-for="city in featuredCities" :key="city.slug"
                :href="getPageUrl(`/${city.slug}`)">
                <div class="app-header__city-bg" :style="{ backgroundImage: `url(${city.image})` }">
                </div>
                <div class="app-header__city-overlay"></div>
                <div class="app-header__city-name">{{ city.name }}</div>
              </a>
            </div>

          </div>
        </div>
      </transition>
    </header>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'AppHeader',
  components: {
  },
  props: {
    headerTheme: {
      type: String,
      default: 'light' // 'light' or 'dark'
    },
  },
  computed: {
    ...mapGetters({
      deviceType: 'deviceType',
      domainConfig: 'domainConfig',
      livableCityData: 'livableCityData',
    }),
    isTransparentPage () {
      return this.$route.path === '/' || this.$route.name === 'home' || this.$route.name === 'cities' || this.$route.name === 'collections';
    },
    computedHeaderTheme () {
      // If explicitly set via prop, use that value
      if (this.headerTheme && this.headerTheme !== 'light') {
        return this.headerTheme;
      }
      // Auto-detect based on page
      if (this.isTransparentPage) {
        return 'light'; // Will be overridden by app-header--home
      }
      return 'dark'; // Non-home pages use dark theme
    },
    featuredCities () {
      const cities = this.livableCityData?.cities || []
      if (cities.length <= 3) {
        return cities
      }
      const seed = (this.$route.path || '/').split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0)
      const rotated = cities.slice(seed % cities.length).concat(cities.slice(0, seed % cities.length))
      return rotated.slice(0, 3)
    },
    siteNameText () {
      return this.domainConfig?.siteName || 'SITE_NAME'
    },
    navExploreText () {
      return 'Explore Cities'
    },
    navMenuAbout () {
      return 'About Us'
    },
    navMenuTerms () {
      return 'Terms of Service'
    },
    navMenuPrivacy () {
      return 'Privacy Policy'
    },
    navMenuDisclaimer () {
      return 'Disclaimer'
    },
  },
  data () {
    return {
      isMenuOpen: false,
      isScrolled: false,
    };
  },
  mounted () {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll(); // Init state based on current scroll position
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll () {
      this.isScrolled = window.scrollY > 50;
    },
    getPageUrl (path) {
      if (this.$route.params.channel) {
        return `/${this.$route.params.channel}${path}`;
      }
      return path;
    },
    toggleMenu () {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu () {
      if (this.isMenuOpen) {
        this.isMenuOpen = false;
      }
    },
    isRouteActive (link) {
      return this.$route.path === link;
    }
  },
  watch: {
    '$route' () {
      this.isMenuOpen = false;
    }
  }
};
</script>

<style lang="scss">
@import '~@/css/variables.scss';

.app-header-wrapper {
	width: 100%;
}

.app-header-placeholder {
	height: rem(60);
	width: 100%;
}

.app-header {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	z-index: 100;
	height: rem(60);
	background-color: #fff;
	transition: background-color 0.3s ease;

	// Reset anchor styles
	a {
		text-decoration: none;
	}

	&__main {
		display: flex;
		align-items: center;
		justify-content: space-between;
		height: rem(60);
		padding: 0 rem(16);
	}

	// Mobile Layout: Left group (menu + sitename with 8px gap)
	&__mobile-left {
		display: flex;
		align-items: center;
		gap: rem(8);

		.app-header__logo-text {
			font-family: $font-family-serif;
			font-size: rem(20);
			font-weight: 700;
			text-transform: uppercase;
			white-space: nowrap;
			color: #000;
		}
	}

	// Left Section (for desktop)
	&__left {
		flex: 1;
		display: flex;
		justify-content: flex-start;
	}

	&__menu-btn {
		background: none;
		border: none;
		cursor: pointer;
		padding: rem(8);
		margin-left: rem(-8);
	}

	&__icon {
		width: rem(24);
		height: rem(24);
	}

	// Logo
	&__logo {
		flex: 1;
		display: flex;
		justify-content: center;
		text-decoration: none;

		&-text {
			color: #000;
			font-family: $font-family-serif;
			font-size: rem(20);
			font-weight: 700;
			text-transform: uppercase;
			white-space: nowrap;

			&--white {
				color: #fff;
				text-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.16);
			}
		}
	}

	&--home {
		background-color: transparent !important;

		&.app-header--scrolled {
			background-color: #000 !important;
		}

		.app-header__logo-text {
			color: #fff;
			text-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.3);
		}

		.app-header__cta-btn {
			background-color: #fecd45;
			border: none;
			color: #000;
			box-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.16);
		}

		.app-header__mobile-left .app-header__logo-text {
			color: #fff;
			text-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.3);
		}
	}

	// Dark theme header (for non-home pages)
	&--dark {
		background-color: #000;

		.app-header__logo-text {
			color: #fff;
		}

		.app-header__cta-btn {
			background-color: #fecd45;
			border: none;
			color: #000;
		}

		.app-header__mobile-left .app-header__logo-text {
			color: #fff;
		}
	}

	// Right Section
	&__right {
		flex: 1;
		display: flex;
		justify-content: flex-end;
	}

	&__cta-btn {
		display: flex;
		align-items: center;
		gap: rem(8);
		text-decoration: none;
		color: #000;
		font-family: $font-family-serif;
		font-weight: 500;
		border: 1px solid rgba(0, 0, 0, 0.1);
		height: rem(32);
		padding: 0 rem(16);
		border-radius: rem(100);
		font-size: rem(14);
		white-space: nowrap;

		&--yellow {
			background-color: #fecd45;
			border: none;
			box-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.16);

			.app-header__cta-icon {
				font-size: rem(12);
			}
		}

		.app-header__cta-icon {
			font-size: rem(16);
		}
	}

	// --- Overlay Styles ---
	&__backdrop {
		position: fixed;
		inset: 0;
		z-index: 150;
		background: transparent;
	}

	&__overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 50vh;
		background: #000;
		z-index: 200;
		display: flex;
		flex-direction: column;
	}

	&__overlay-left {
		display: flex;
		align-items: center;
		gap: rem(8);
	}

	&__overlay-right {
		display: flex;
		align-items: center;
	}

	&__overlay-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		height: rem(60);
		padding: 0 rem(16);
		background-color: #000;
		flex-shrink: 0;
	}

	&__overlay-content {
		flex: 1;
		padding: rem(20);
		display: flex;
		flex-direction: column;
		// Add backdrop filter if supported, though solid black is safer for now
	}

	&__menu-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: rem(12);
		margin-bottom: rem(32);
	}

	&__menu-pill {
		border: 1px solid #fecd45;
		border-radius: rem(100);
		height: rem(40);
		display: flex;
		align-items: center;
		justify-content: center;
		gap: rem(8);
		color: #fecd45;
		font-family: $font-family-serif;
		font-size: rem(14);
		transition: background 0.2s, transform 0.2s ease;
		width: fit-content;
		padding: 0 rem(16);

		&:hover {
			background: rgba(254, 205, 69, 0.1);
		}
	}

	&__menu-icon {
		width: rem(16);
		height: rem(16);
		// Assuming icons are colored or need filter
		// If SVGs are black, we might need to invert or use CSS filter to make them yellow
		// But let's assume assets are correct for now or use filter
		filter: invert(86%) sepia(31%) saturate(972%) hue-rotate(320deg) brightness(101%) contrast(105%); // Approximate Gold filter if icons are black
	}

	&__featured {
		display: grid;
		grid-template-columns: 1fr 1fr; // Mobile side by side
		gap: rem(12);
		margin-bottom: rem(32);
	}

	&__city-card {
		position: relative;
		border-radius: rem(24);
		overflow: hidden;
		aspect-ratio: 1; // Square cards for mobile grid to fit
		box-shadow: 0 rem(4) rem(12) rgba(0, 0, 0, 0.3);
		transition: transform 0.2s ease;
	}

	&__city-bg {
		width: 100%;
		height: 100%;
		background-size: cover;
		background-position: center;
	}

	&__city-overlay {
		position: absolute;
		inset: 0;
		background: linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.8) 100%);
	}

	&__city-name {
		position: absolute;
		bottom: rem(16);
		left: rem(16);
		color: #fff;
		font-family: $font-family-serif;
		font-weight: 700;
		font-size: rem(18);
		text-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.5);
	}

	&__collections-btn {
		margin-top: auto; // Push to bottom
		background: #fecd45;
		border-radius: rem(100);
		height: rem(56);
		display: flex;
		align-items: center;
		justify-content: center;
		gap: rem(12);
		color: #000;
		font-family: $font-family-serif;
		font-size: rem(20);
		font-weight: 700;
		box-shadow: 0 rem(4) rem(12) rgba(254, 205, 69, 0.3);

		&:hover {
			transform: translateY(rem(-2));
		}
	}

	&__collections-icon {
		width: rem(24);
		height: rem(24);
	}
}

// PC/Desktop Overrides
.detect_deviece_pc,
.detect_deviece_desktop {
	.app-header {
		&__main {
			padding: 0 rem(180);
		}

		// Hide mobile layout on desktop
		&__mobile-left {
			display: none;
		}

		&__logo-text {
			font-size: rem(24);
		}

		&__overlay {
			height: 50vh;
			border-top: 1px solid rgba(255, 255, 255, 0.1);
			background: linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.6) 100%);
			backdrop-filter: blur(10px);
		}

		&__overlay-header {
			padding: 0 rem(180);
			height: rem(60);
			justify-content: center;
			gap: rem(40);

			.app-header__overlay-left {
				flex: 1;
				justify-content: flex-start;
			}

			.app-header__logo-text {
				flex: 0 auto;
				text-align: center;
			}

			.app-header__overlay-right {
				flex: 1;
				justify-content: flex-end;
			}
		}

		&__overlay-content {
			padding: rem(40) rem(180);
			flex-direction: row;
			justify-content: space-between;
			align-items: flex-start;
		}

		&__menu-grid {
			// Desktop: 1 column
			display: flex;
			flex-direction: column;
			width: rem(200);
			gap: rem(24);
		}

		&__featured {
			grid-template-columns: repeat(3, 1fr);
			gap: rem(24);
		}

		&__city-card {
			width: rem(196);
			height: rem(196);
		}

		&__menu-pill:hover,
		&__city-card:hover {
			transform: translateY(rem(-4));
		}

		&__collections-btn {
			display: none; // Hide on Desktop if not in design? Or maybe show differently
		}
	}
}

// Mobile Overrides
.detect_deviece_mobile {
	.app-header {
		padding: 0;

		&__overlay {
			background: linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.6) 100%);
			bottom: 0;
			height: auto;
			min-height: 100vh;
			max-height: 100vh;
			box-sizing: border-box;
			overflow-y: auto;
			-webkit-overflow-scrolling: touch;
		}

		&__overlay-header {
			.app-header__logo-text {
				font-size: rem(20);
			}
		}

		&__overlay-content {
			padding: rem(24) rem(16);
			padding-bottom: rem(32);
			overflow: visible;
		}

		&__featured {
			display: flex;
			flex-direction: row;
			gap: rem(12);
			overflow-x: auto;
			margin-right: rem(-16);
			padding-right: rem(16);
			scrollbar-width: none;
			-ms-overflow-style: none;

			&::-webkit-scrollbar {
				display: none;
			}
		}

		&__city-card {
			flex-shrink: 0;
			width: rem(196);
			height: rem(196);
			aspect-ratio: auto;
		}
	}
}

// Transition
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}
</style>
